import FormCheckSquare from 'components/LM/common/FormCheckSquare';
import ModalDisplay from "components/LM/common/ModalDisplay";
import VoiceProfile from "components/LM/Voice/Profile";
import VoiceUrl from 'components/LM/Voice/Url';
import Voice from "components/LM/Voice/Voice";
import K2Api from "lib/ajax/K2Api";
import { useEffect, useState } from "react";
import { Button, ButtonToolbar, Card, Col, ListGroup, Row, ToggleButton } from "react-bootstrap";
import Alert from 'react-bootstrap/Alert';
import { useHistory } from 'react-router-dom';

import { Loader } from "@launchmetrics/lm-ui";
import './VoiceMerge.scss';
const K2_API = new K2Api();
const SOCIAL_PROVIDERS = ["instagram", "youtube", "tiktok", "twitter", "facebook", "weibo", "douyin", "xiaohongshu", "bilibili", "wechat_public"];

function VoiceMerge({ voiceIds }) {
    const [processing, setProcessing] = useState(false);
    const [voices, setVoices] = useState(null);
    const [mainVoiceId, setMainVoiceId] = useState(null);
    const [mainVoiceType, setMainVoiceType] = useState({});
    const [mainVoiceCountry, setMainVoiceCountry] = useState({});
    const [showModal, setShowModalState] = useState(false);
    const [error, setError] = useState(null);
    const history = useHistory()

    const isSuggestedVoicesProvided = history.location.state && history.location.state.voices;

    useEffect(() => {
        const fetchData = async () => {
            setError(null);
            setProcessing(true);

            const promises = voiceIds.map(id => K2_API.get(`/voices/${id}`));
            let responses;
            try {
                responses = await Promise.all(promises);
            } catch (err) {
                if (err.response.status === 404) {
                    setError("Voice not found");
                } else {
                    setError(err);
                }
            } finally {
                setProcessing(false);
            }

            if (responses) {
                const voices = processResponse(responses.map(response => response.data));

                if (voices.length <= 1) setError("You need more than 1 voice to process with the merge");
                if (voices.length > 5) setError("You cannot merge more than 5 voices at once");

                setVoices(voices);
            }
        };

        if (isSuggestedVoicesProvided) {
            const providedVoices = history.location.state.voices;
            if (providedVoices) {
                const voices = processResponse(providedVoices);

                if (voices.length <= 1) setError("You need more than 1 voice to process with the merge");
                if (voices.length > 5) setError("You cannot merge more than 5 voices at once");

                setVoices(voices);
            }
        } else {
            fetchData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voiceIds, isSuggestedVoicesProvided]);

    const onChange = (ev) => {
        if (mainVoiceId === ev.target.value) {
            checkVoice(ev.target.value, false);
            setMainVoiceId(null);
        } else {
            if (mainVoiceId) {
                checkVoice(mainVoiceId, false);
            }
            checkVoice(ev.target.value, true);
            setMainVoiceId(ev.target.value);

        }
    };

    const checkVoice = (voiceId, action) => {
        const voice = voices.find(v => v.id === voiceId);
        const sources = ["socialmedia", "medias.print", "medias.online", "urls"];
        sources.forEach(source => {
            if (source.includes('medias')) {
                const mediaType = source.split('.')[1];
                voice.medias[mediaType].forEach(item => {
                    item.enabled = action;
                });
            } else {
                voice[source].forEach(s => {
                    s.enabled = action;
                });
            }
        });

        voice.labels.forEach(label => {
            label.enabled = action;
        });
        if (voice.country) voice.country.enabled = action;

        if (action === true) {
            setMainVoiceType({ id: voice.id, value: voice.type });
            if (voice.country) {
                setMainVoiceCountry({ id: voice.id, value: { id: voice.country.id, name: voice.country.name } });
            }
        } else {
            setMainVoiceType({});
            setMainVoiceCountry({});
        }

        setVoices([...voices]);
    };

    const onToggle = (type, id, enabled, voiceId) => {
        let sourceType;
        if (type !== "print" && type !== "online" && type !== "other") {
            sourceType = "socialmedia";
        } else if (type === "print" || type === "online") {
            sourceType = `medias.${type}`;
        }
        else {
            sourceType = "urls";
        }

        const voice = voices.find(v => v.id === voiceId);
        let source;
        if (sourceType.includes('medias')) {
            const mediaType = sourceType.split('.')[1];
            source = voice.medias[mediaType].find(s => s.id === id);
        } else {
            source = voice[sourceType].find(s => s.id === id);
        }
        source.enabled = enabled;
        setVoices([...voices]);
    };

    const onToggleLabel = (labelId, voiceId, enabled) => {
        const voice = voices.find(v => v.id === voiceId);
        const label = voice.labels.find(l => l.id === labelId);
        label.enabled = enabled;
        setVoices([...voices]);
    }

    const onToggleVoiceDetails = (voiceId, ev, type) => {
        if (type === "country") {
            const voice = voices.find(v => v.id === voiceId);
            setMainVoiceCountry({ id: voiceId, value: { id: ev.target.value, name: voice.country.name } });
        } else {
            setMainVoiceType({ id: voiceId, value: ev.target.value });
        }
    }

    function buildMergeRequest(voices, mainVoiceId) {
        const sourceVoiceIds = voices
            .filter(voice => voice.id !== mainVoiceId)
            .map(voice => voice.id);


        const mainVoice = voices.find(voice => voice.id === mainVoiceId);

        return {
            source_voice_id: sourceVoiceIds,
            profiles: mergeSocialMediaProfiles(voices),
            medias: {
                print: getEnabledMergeItems(voices.map(voice => voice.medias.print)),
                online: getEnabledMergeItems(voices.map(voice => voice.medias.online)),
            },
            urls: getEnabledMergeItems(voices.map(voice => voice.urls)),
            labels: [...new Set(getEnabledMergeItems(voices.map(voice => voice.labels)))],
            voice: {
                name: mainVoice.name,
                ...(Object.keys(mainVoiceCountry).length > 0 && { countryIso: mainVoiceCountry.value.id }),
                type: mainVoiceType.value
            }
        };
    }

    const onSubmit = (ev) => {
        setShowModalState(true);
    };

    const onConfirmMerge = async () => {
        const request = buildMergeRequest(voices, mainVoiceId);
        await K2_API.post(`/voices/${mainVoiceId}/merge`, request)
            .then(() => {
                history.push(`/voices/${mainVoiceId}`);
            }).catch(error => {
                setError(error);
            })
    }

    if (error) {
        return <Alert variant="danger">{error.toString()}</Alert>;
    }

    if (processing || !voices) {
        return <Loader />;
    }

    return (
        <div>
            {voices.map(voice => (
                <VoiceMergeInformation
                    key={voice.id}
                    voice={voice}
                    mainVoiceId={mainVoiceId}
                    onChange={onChange}
                    onToggleLabel={onToggleLabel}
                    mainVoiceCountry={mainVoiceCountry}
                    onToggleVoiceDetails={onToggleVoiceDetails}
                    mainVoiceType={mainVoiceType}
                    onToggle={onToggle}
                />
            ))}
            <ButtonToolbar className="float-right">
                <Button
                    color="primary"
                    type="submit"
                    size="lg"
                    className="mb-3"
                    disabled={mainVoiceId === null || processing}
                    onClick={(ev) => onSubmit(ev)}
                >
                    Merge
                </Button>
            </ButtonToolbar>
            {mainVoiceId && voices && <Modal showModal={showModal} voices={voices} mainVoiceId={mainVoiceId} setShowModalState={setShowModalState} onConfirmMerge={onConfirmMerge} mainVoiceCountry={mainVoiceCountry} mainVoiceType={mainVoiceType} />}
        </div>
    );
}

function processResponse(response) {
    return response.map(voice => {
        return {
            ...voice,
            socialmedia: Object.entries(voice.socialmedia).flatMap(([type, value]) => value.map(v => ({ ...v, type })))
        };

    })
}

function getEnabledMergeItems(array, isfullObject) {
    return array.map(item => item.filter(subItem => subItem.enabled).map(subItem => isfullObject ? subItem : subItem.id)).flat();
}

function mergeSocialMediaProfiles(voices, isfullObject) {
    const socialMergeProfiles = {};
    SOCIAL_PROVIDERS.forEach(provider => {
        socialMergeProfiles[provider] = [];
    });

    return voices.reduce((acc, voice) => {
        voice.socialmedia.forEach(profile => {
            if (profile.enabled) {
                acc[profile.type].push(isfullObject ? profile : profile.id);
            }
        });
        return acc;
    }, socialMergeProfiles);
}

function Modal(props) {
    const { showModal, voices, mainVoiceId, setShowModalState, onConfirmMerge, mainVoiceCountry, mainVoiceType } = props;
    const mainVoice = voices.find(v => v.id === mainVoiceId);

    const mergeVoice = {
        ...mainVoice,
        ...(Object.keys(mainVoiceCountry).length > 0 ? {
            country: {
                id: mainVoiceCountry.value.id,
                name: mainVoiceCountry.value.name,
            }
        } : {}),
        type: mainVoiceType.value,
        labels: voices.flatMap(voice => voice.labels)
            .filter(label => label.enabled)
            .reduce((unique, label) => {
                return unique.some(u => u.id === label.id) ? unique : [...unique, label];
            }, []),
        medias: {
            print: getEnabledMergeItems(voices.map(voice => voice.medias.print), true),
            online: getEnabledMergeItems(voices.map(voice => voice.medias.online), true),
        },
        urls: getEnabledMergeItems(voices.map(voice => voice.urls), true),
        socialmedia: mergeSocialMediaProfiles(voices, true)
    };

    return (
        <>
            <ModalDisplay
                isVisible={showModal}
                headerText={"Ensure that this is the correct voice merge"}
                contentText={<Voice voice={mergeVoice} editable={false} />}
                size="xl"
                confirmButtonText={"Confirm Merge"}
                onCancel={() => setShowModalState(!showModal)}
                onConfirm={() => onConfirmMerge()}
            />
        </>
    );
}

function Labels(props) {
    const { labels, voiceId, onToggleLabel } = props;

    const labelsItems = [];
    labels.forEach((label) => {
        labelsItems.push(
            <ListGroup.Item key={`${label.id}_${label.name}`} className="border-0">
                <FormCheckSquare
                    className="ml-2 mr-2"
                    width="18"
                    checked={label.enabled ?? false}
                    onChange={v => onToggleLabel(label.id, voiceId, v)} />
                <span className="ml-2 mr-2">{label.name}</span>
            </ListGroup.Item>
        );
    });
    return (
        <ListGroup horizontal>
            {labelsItems}
        </ListGroup>
    );
}

function VoiceMergeInformation({ voice, mainVoiceId, onChange, onToggleLabel, mainVoiceCountry, onToggleVoiceDetails, mainVoiceType, onToggle }) {
    return (
        <Card key={voice.id} className="voice-merge mb-4">
            <Card.Body>
                <Card.Title as="h2">
                    <span className='mr-2'>{voice.id} {voice.name}</span>
                    <ToggleButton type="radio" variant="unknown" size="lg" className="radio ml-2" onChange={onChange} value={voice.id} checked={mainVoiceId === voice.id}> Main</ToggleButton>
                </Card.Title>
                <Row className="mb-5" >
                    <Col>
                        <ListGroup>
                            <h3>Labels</h3>
                            <Labels labels={voice.labels} voiceId={voice.id} onToggleLabel={onToggleLabel} />
                        </ListGroup>
                    </Col>
                    <Col>
                        <h3>Country</h3>
                        {voice.country ? <ToggleButton
                            type="radio"
                            variant="unknown"
                            size="lg"
                            className="radio ml-2"
                            onChange={v => onToggleVoiceDetails(voice.id, v, "country")}
                            value={voice.country.id}
                            checked={voice.id === mainVoiceCountry.id && voice.country.id === mainVoiceCountry.value.id}> {voice.country.name}</ToggleButton> : ""}
                    </Col>
                    <Col>
                        <h3>Type</h3>
                        <ToggleButton
                            type="radio"
                            variant="unknown"
                            size="lg"
                            className="radio ml-2"
                            onChange={v => onToggleVoiceDetails(voice.id, v, "type")}
                            value={voice.type}
                            checked={voice.id === mainVoiceType.id && voice.type === mainVoiceType.value}> {voice.type}</ToggleButton>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <ListGroup>
                            <h3>Social</h3>
                            <VoicesSourceItems
                                sources={voice.socialmedia}
                                voiceId={voice.id}
                                onToggle={onToggle}
                            />
                        </ListGroup>
                    </Col>
                    <Col>
                        <ListGroup>
                            <h3>Print</h3>
                            <VoicesSourceItems
                                sources={voice.medias.print}
                                voiceId={voice.id}
                                type={"print"}
                                onToggle={onToggle}
                            />
                        </ListGroup>
                    </Col>
                    <Col>
                        <ListGroup>
                            <h3>Online</h3>
                            <VoicesSourceItems
                                sources={voice.medias.online}
                                voiceId={voice.id}
                                type={"online"}
                                onToggle={onToggle}
                            />
                        </ListGroup>
                    </Col>
                    <Col>
                        <ListGroup>
                            <h3>Link</h3>
                            <VoicesSourceItems
                                sources={voice.urls}
                                voiceId={voice.id}
                                type={"other"}
                                onToggle={onToggle}
                            />
                        </ListGroup>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}


function VoicesSourceItems(props) {
    const { sources, voiceId, type, onToggle } = props;

    if (!sources || sources.length === 0) {
        return [];
    }
    const voiceSourcesItems = [];

    if (type === "other") {
        sources.forEach((source) => {
            const voiceSourceUrl = (
                <VoiceUrl
                    url={source}
                    type={type}
                    checked={source.enabled ?? false}
                    editMode={true}
                    editType={true}
                    onToggle={v => onToggle(type, source.id, v, voiceId)}
                />
            );

            voiceSourcesItems.push(
                <ListGroup.Item key={`${source.id}_${type}`} className="border-0">{voiceSourceUrl}</ListGroup.Item>
            );
        });
    } else {
        sources.forEach((source) => {
            const sourceType = type ? type : source.type;
            const voiceSource = (
                <VoiceProfile
                    type={sourceType}
                    profile={source}
                    editMode={true}
                    editType={true}
                    checked={source.enabled ?? false}
                    onToggle={v => onToggle(type, source.id, v, voiceId)}
                />
            );

            voiceSourcesItems.push(
                <ListGroup.Item key={`${source.id}_${sourceType}`} className="border-0">{voiceSource}</ListGroup.Item>
            );
        });
    }

    return voiceSourcesItems;
}

export default VoiceMerge;
